import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Space,
  Button,
  Dropdown,
  Avatar,
} from '@lockerpm/design';

import {
  LogoutOutlined,
  PlusOutlined,
  LockOutlined
} from '@ant-design/icons'

import authServices from '../../services/auth';
import projectServices from '../../secrets-web/src/services/project';
import userServices from '../../services/user';

import storeActions from '../../store/actions';
import global from '../../config/global';
import { updateLocale } from '../../secrets-web/src/utils/common';

function DropdownMenu() {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.auth.userInfo);
  const workspaces = useSelector((state) => state.workspace.workspaces);
  const selectedWorkspace = useSelector((state) => state.workspace.selectedWorkspace);

  const AvatarIcon = useMemo((size = 32) => {
    if (userInfo?.avatar) {
      return <Avatar
        size={size}
        src={userInfo?.avatar}
      />
    }
    return <Avatar
      size={size}
      style={{
        backgroundColor: 'var(--sidebar-menu-group-color)',
        color: 'var(--sidebar-menu-active-background-color)'
      }}
    >
      {userInfo?.full_name?.slice(0, 1)?.toUpperCase() || 'S'}
    </Avatar>
  }, [userInfo])

  const items = useMemo(() => {
    return [
      {
        key: 'account',
        icon: AvatarIcon,
        label: <div className='account-info'>
          <div>{userInfo?.full_name}</div>
        </div>,
      },
      {
        key: 'workspace',
        label: <span>
          <span className='font-semibold'>
            {t('common.workspace')}:</span> <span>{selectedWorkspace?.name}
          </span>
        </span>,
        children: [
          ...workspaces.map((w) => ({
            key: `workspace_${w.id}`,
            label: <p className={w.id === selectedWorkspace?.id ? 'text-primary' : ''}>
              {w.name}
            </p>,
          })),
          {
            type: 'divider'
          },
          {
            key: 'create',
            label: <div
              className='flex items-center'
              onClick={() => global.navigate('CREATE_WORKSPACE')}
            >
              <PlusOutlined className='mr-2' />
              {t('button.new_workspace')}
            </div>
          }
        ]
      },
      {
        key: 'language',
        label: <span>
          <span className='font-semibold'>{t('common.language')}:</span> <span>English (US)</span>
        </span>,
        children: global.constants.LANGUAGES.map((l) => ({
          key: l.value,
          label: t(l.label),
          disabled: l.disabled
        }))
      },
      {
        type: 'divider',
      },
      {
        key: 'locker_help_center',
        label: <a href={global.urls.SUPPORT_URL} target="_blank">
          {t('sidebar.locker_help_center')}
        </a>
      },
      {
        key: 'locker_community_forum',
        label: <a href={global.urls.FORUM_URL} target="_blank">
          {t('sidebar.locker_community_forum')}
        </a>
      },
      {
        key: 'terms_policy',
        label: <a href={global.urls.TERMS_URL} target="_blank">
          {t('sidebar.terms_policy')}
        </a>
      },
      {
        type: 'divider',
      },
      {
        key: 'lock',
        icon: <LockOutlined />,
        label: <span>{t('sidebar.lock_your_account')}</span>
      },
      {
        key: 'logout',
        danger: true,
        icon: <LogoutOutlined />,
        label: <span>{t('sidebar.logout')}</span>
      },
    ].filter((item) => !item.hide)
  }, [
    selectedWorkspace,
    workspaces,
    userInfo
  ])


  const dropdownClick = async (item) => {
    if (item.key === 'account') {
      global.navigate('ACCOUNT_DETAILS')
    } else if (item.key === 'logout') {
      authServices.logout();
    } else if (item.key === 'lock') {
      authServices.redirect_login();
    } else if (item.key.includes('workspace_')) {
      const workspace_id = item.key.split('workspace_')[1];
      const projects = await projectServices.list({ paging: 0, organization_id: workspace_id });
      global.store.dispatch(storeActions.updateProjects(projects));
      global.navigate('PROJECTS', { workspace_id })
    } else if (global.constants.LANGUAGES.find(l => l.value === item.key)) {
      updateLocale(item.key);
      userServices.update_me({ customer_language: item.key })
    }
  }

  return (
    <Dropdown
      menu={{
        items,
        defaultSelectedKeys: [],
        onClick: dropdownClick
      }}
      placement="bottomLeft"
      trigger={'click'}
    >
      <Space wrap>
        <Button
          type='text'
          className='avatar-button'
          shape='circle'
          icon={AvatarIcon}
        />
      </Space>
    </Dropdown>
  );
}

export default DropdownMenu;
